import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  BookOpenIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import BookUploadModal from '../components/BookUploadModal';
import { canUploadBooksHere } from '../utils/uploadPermissions';
import { Tooltip } from 'react-tooltip';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileReaderModal } from '../components/common/MobileReaderModal';

const Library: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['all']);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [selectedBookTitle, setSelectedBookTitle] = useState<string | null>(null);
  const canUpload = canUploadBooksHere(user);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/books/categories');
      // Prepend 'all' to the categories list
      setCategories(['all', ...response.data]);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Fallback to hardcoded categories on error
      setCategories(['all', 'Psychology', 'Mathematics', 'Computer Science', 'Chemistry', 'Economics', 'Physics', 'Biology']);
    }
  };

  // Fetch books from API with optional category filter
  const fetchBooks = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await api.get('/api/books/', { params });
      setBooks(response.data);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch books whenever category changes
  useEffect(() => {
    fetchBooks();
  }, [selectedCategory]);

  // Client-side filtering only for search (category filtering is server-side)
  const filteredBooks = books.filter(book => {
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         book.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            My Library
          </h1>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
            Manage and access all your textbooks in one place
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          {/* Search */}
          <div className="relative w-full sm:w-96">
            <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search books or authors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 rounded-xl transition-all ${
                isDarkMode
                  ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-400 focus:border-cyan-500'
                  : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-cyan-500'
              } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
            />
          </div>

          {/* Upload Button - gated by tenant context + role + feature flag.
              See utils/uploadPermissions.ts for the rule. */}
          {canUpload && (
            <button
              onClick={() => setShowUploadModal(true)}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl transition-colors font-medium border ${
                isDarkMode
                  ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-500/60'
                  : 'border-cyan-500/60 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-600'
              }`}
            >
              <DocumentArrowUpIcon className="h-5 w-5" />
              <span>{user?.is_admin ? 'Upload Textbook' : 'Upload a book'}</span>
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                selectedCategory === category
                  ? 'text-white'
                  : isDarkMode
                    ? 'bg-slate-800/50 text-slate-300 hover:bg-slate-800 border border-slate-700'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
              style={selectedCategory === category ? {
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
              } : {}}
            >
              {category === 'all' ? 'All Books' : category}
            </button>
          ))}
        </div>

        {/* Books Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
          </div>
        ) : filteredBooks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBooks.map((book) => {
              // Generate a gradient color based on book ID
              const gradients = [
                'from-cyan-400 to-blue-500',
                'from-emerald-400 to-teal-500',
                'from-blue-400 to-violet-500',
                'from-amber-400 to-orange-500',
                'from-pink-400 to-rose-500',
                'from-violet-400 to-purple-500',
              ];
              const coverColor = gradients[book.id % gradients.length];
              
              // Format last read time
              const formatLastRead = (dateStr: string | null) => {
                if (!dateStr) return 'Not started';
                const date = new Date(dateStr);
                const now = new Date();
                const diff = now.getTime() - date.getTime();
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const days = Math.floor(hours / 24);
                
                if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
                if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
                return 'Just now';
              };
              
              return (
                <div
                  key={book.id}
                  className="group"
                >
                  <div className={`rounded-2xl overflow-hidden transition-all ${
                    isDarkMode
                      ? 'bg-slate-900/50 border border-slate-700/50 hover:border-slate-600'
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg'
                  }`}>
                    {/* Book Cover */}
                    {book.cover_presigned_url ? (
                      <div className="h-48 relative overflow-hidden bg-slate-800">
                        <img
                          src={book.cover_presigned_url}
                          alt={book.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to gradient if image fails
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className={`hidden h-48 bg-gradient-to-br ${coverColor} flex items-center justify-center relative overflow-hidden`}>
                          <BookOpenIcon className="h-24 w-24 text-white opacity-20 absolute" />
                          <div className="relative z-10 text-center px-4">
                            <h3 className="text-white font-bold text-lg leading-tight">{book.title}</h3>
                          </div>
                        </div>
                        {/* Progress Overlay */}
                        {book.progress_percentage > 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 p-2">
                            <div className="w-full bg-white bg-opacity-30 rounded-full h-1.5">
                              <div
                                className="h-1.5 rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${book.progress_percentage}%`,
                                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`h-48 bg-gradient-to-br ${coverColor} flex items-center justify-center relative overflow-hidden`}>
                        <BookOpenIcon className="h-24 w-24 text-white opacity-20 absolute" />
                        <div className="relative z-10 text-center px-4">
                          <h3 className="text-white font-bold text-lg leading-tight">{book.title}</h3>
                        </div>
                        {/* Progress Overlay */}
                        {book.progress_percentage > 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 p-2">
                            <div className="w-full bg-white bg-opacity-30 rounded-full h-1.5">
                              <div
                                className="h-1.5 rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${book.progress_percentage}%`,
                                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Book Info */}
                    <div className="p-4">
                      <p className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                        {book.title}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {book.category || 'Uncategorized'}
                      </p>
                      <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {book.author}
                      </p>

                      {/*
                        Status Badge — non-approved books normally get filtered out of
                        the library query server-side, so this branch is mostly a safety
                        net for edge cases (admin bypass, stale cache). Only the statuses
                        the backend can actually produce are handled here.
                      */}
                      {book.status && book.status !== 'approved' && (
                        <div className="mb-2">
                          {(book.status === 'pending' || book.status === 'in_review') && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800">
                              Under review
                            </span>
                          )}
                          {book.status === 'rejected' && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-100 text-red-800 cursor-help"
                              data-tooltip-id="library-tooltip"
                              data-tooltip-content={book.rejection_reason || 'Rejected'}
                            >
                              Rejected
                            </span>
                          )}
                        </div>
                      )}

                      <div className={`flex items-center justify-between text-xs mb-3 ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                      }`}>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{formatLastRead(book.last_read_at)}</span>
                        </div>
                        {book.progress_percentage > 0 && (
                          <span className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            {Math.round(book.progress_percentage)}%
                          </span>
                        )}
                      </div>

                      {/* Action Buttons — disabled for non-approved books */}
                      {(!book.status || book.status === 'approved') ? (
                        <div className="flex gap-2 min-w-0">
                          <button
                            onClick={() => {
                              if (isMobile) {
                                setSelectedBookTitle(book.title);
                                setShowMobileModal(true);
                              } else {
                                navigate(`/dhyana/${book.id}`);
                              }
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl font-medium text-sm transition-all text-white"
                            style={{
                              background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 0 20px rgba(6, 182, 212, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            data-tooltip-id="library-tooltip"
                            data-tooltip-content="Start reading this book"
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            Open
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/library/${book.id}/notes`);
                            }}
                            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-medium text-sm transition-all ${
                              isDarkMode
                                ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            data-tooltip-id="library-tooltip"
                            data-tooltip-content="View all notes"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            <span className="sr-only">Notes</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/bookmarks?book=${book.id}`);
                            }}
                            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-medium text-sm transition-all ${
                              isDarkMode
                                ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            data-tooltip-id="library-tooltip"
                            data-tooltip-content="View bookmarks"
                          >
                            <BookmarkIcon className="h-4 w-4" />
                            <span className="sr-only">Bookmarks</span>
                          </button>
                        </div>
                      ) : (
                        <div className={`text-center py-2 text-xs rounded-xl ${
                          isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {book.status === 'rejected' ? 'Upload was rejected' : 'Awaiting admin review'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add New Book Card — same gate as the header upload button. */}
            {canUpload && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="group cursor-pointer"
              >
                <div className={`rounded-2xl overflow-hidden transition-all h-full min-h-[320px] flex flex-col items-center justify-center border-2 border-dashed ${
                  isDarkMode
                    ? 'bg-slate-800/30 border-slate-700 hover:border-cyan-500/50 hover:bg-slate-800/50'
                    : 'bg-gray-50 border-gray-300 hover:border-cyan-400 hover:bg-cyan-50'
                }`}>
                  <PlusIcon className={`h-12 w-12 mb-3 transition-colors ${
                    isDarkMode
                      ? 'text-slate-400 group-hover:text-cyan-400'
                      : 'text-gray-400 group-hover:text-cyan-600'
                  }`} />
                  <span className={`font-medium transition-colors ${
                    isDarkMode
                      ? 'text-slate-400 group-hover:text-cyan-400'
                      : 'text-gray-600 group-hover:text-cyan-600'
                  }`}>
                    {user?.is_admin ? 'Add New Book' : 'Upload a Book'}
                  </span>
                </div>
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpenIcon className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No books found
            </h3>
            <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <BookUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          setShowUploadModal(false);
          fetchBooks(); // Refresh the book list
        }}
        isAdmin={user?.is_admin}
      />

      {/* Tooltip */}
      <Tooltip
        id="library-tooltip"
        positionStrategy="fixed"
        style={{
          backgroundColor: '#06B6D4',
          color: '#fff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          zIndex: 9999
        }}
      />

      {/* Mobile Reader Modal */}
      <MobileReaderModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        bookTitle={selectedBookTitle || undefined}
      />
    </Layout>
  );
};

export default Library;
